import type {
  CreateGlobalCreditAccountInput,
  GlobalCreditAccountFragment,
  UpdateGlobalCreditAccountInput,
} from "@src/__generated__/graphql";
import type { AppStore } from "@src/stores/AppStore";
import { required } from "@src/utils/forms/validators";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { FieldState, FormState } from "formstate";
import { action, computed, makeObservable, observable } from "mobx";

type GlobalCreditAccountModalData = {
  globalCreditAccount?: GlobalCreditAccountFragment;
  onSubmit?: (
    data: CreateGlobalCreditAccountInput | UpdateGlobalCreditAccountInput,
  ) => void;
};

export class GlobalCreditAccountModalStore {
  appStore: AppStore;
  readonly modalId = "globalCreditAccountModal";

  @observable
  form: FormState<{
    id: FieldState<string | undefined>;
    title: FieldState<string>;
    note: FieldState<string>;
  }> | null = null;

  modalState = new DisclosureState<GlobalCreditAccountModalData>({
    onOpen: (additionalData) => {
      this.initForm(additionalData?.globalCreditAccount);
    },
    onClose: () => {
      this.form = null;
    },
  });

  constructor(appStore: AppStore) {
    makeObservable(this);
    this.appStore = appStore;
    this.initForm();
  }

  @computed
  get isEditMode(): boolean {
    return !!this.modalState.additionalData?.globalCreditAccount;
  }

  @action.bound
  private initForm(creditAccount?: GlobalCreditAccountFragment) {
    this.form = new FormState({
      id: new FieldState(creditAccount?.id),
      title: new FieldState(creditAccount?.title ?? "").validators(required),
      note: new FieldState(creditAccount?.note ?? ""),
    });
  }

  @action.bound
  async handleSubmit() {
    if (!this.form) return;

    const validation = await this.form.validate();

    if (validation.hasError) return;

    this.modalState.additionalData?.onSubmit?.({
      id: validation.value.id.$,
      title: validation.value.title.$,
      note: validation.value.note.$,
    });
  }
}
